import React, { useState, useEffect, useRef } from 'react';
import { BarChart3, X, CheckCircle, Clock } from 'lucide-react';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useRealtime } from '../../contexts/RealtimeContext';
import toast from 'react-hot-toast';

const PollDisplay = ({ classId, isHost }) => {
  const { currentUser } = useRealtime();
  const [activePoll, setActivePoll] = useState(null);
  const [hasVoted, setHasVoted] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isDismissed, setIsDismissed] = useState(false);
  const unsubscribeRef = useRef(null);
  const timerIntervalRef = useRef(null);

  useEffect(() => {
    if (!classId) return;

    const pollsRef = collection(db, 'webrtcSessions', classId, 'polls');
    const q = query(pollsRef, orderBy('createdAt', 'desc'));

    unsubscribeRef.current = onSnapshot(q, (snapshot) => {
      const polls = [];
      snapshot.forEach((docSnap) => {
        const poll = { id: docSnap.id, ...docSnap.data() };
        // Show both active and ended polls (ended polls show results)
        if (poll.status === 'active' || poll.status === 'ended') {
          polls.push(poll);
        }
      });
      
      if (polls.length > 0) {
        const latestPoll = polls[0];
        
        // If a new poll is created, reset dismissed state
        if (activePoll && activePoll.id !== latestPoll.id) {
          setIsDismissed(false);
        }
        
        // Ensure options is an array
        const pollOptions = Array.isArray(latestPoll.options) 
          ? latestPoll.options 
          : latestPoll.options 
            ? Object.values(latestPoll.options) 
            : [];
        
        const pollWithArrayOptions = {
          ...latestPoll,
          options: pollOptions
        };
        
        setActivePoll(pollWithArrayOptions);
        
        // Check if user has voted
        const userVoted = pollOptions.some(opt => 
          opt && opt.voters && Array.isArray(opt.voters) && opt.voters.includes(currentUser?.uid)
        );
        setHasVoted(userVoted);
        
        // Start timer if poll is active
        if (latestPoll.status === 'active' && latestPoll.createdAt) {
          const startTime = latestPoll.createdAt.toMillis ? latestPoll.createdAt.toMillis() : latestPoll.createdAt;
          const updateElapsedTime = () => {
            const now = Date.now();
            const elapsed = Math.floor((now - startTime) / 1000); // in seconds
            setElapsedTime(elapsed);
          };
          
          // Clear existing interval
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
          }
          
          // Update immediately
          updateElapsedTime();
          
          // Update every second
          timerIntervalRef.current = setInterval(updateElapsedTime, 1000);
        } else {
          // Clear timer if poll is ended
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
          }
          setElapsedTime(0);
        }
      } else {
        setActivePoll(null);
        setHasVoted(false);
        setIsDismissed(false);
        // Clear timer
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
        setElapsedTime(0);
      }
    }, (error) => {
      console.error('Error loading polls:', error);
    });

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [classId, currentUser]);

  const handleVote = async (optionIndex) => {
    if (!activePoll || hasVoted || !currentUser) return;

    try {
      const pollRef = doc(db, 'webrtcSessions', classId, 'polls', activePoll.id);
      const pollOptions = Array.isArray(activePoll.options) 
        ? activePoll.options 
        : activePoll.options 
          ? Object.values(activePoll.options) 
          : [];
      const option = pollOptions[optionIndex];
      
      if (!option) {
        toast.error('Invalid option selected');
        return;
      }
      
      // Update the option in the array
      const updatedOptions = [...pollOptions];
      updatedOptions[optionIndex] = {
        ...updatedOptions[optionIndex],
        votes: (updatedOptions[optionIndex].votes || 0) + 1,
        voters: [...(updatedOptions[optionIndex].voters || []), currentUser.uid]
      };
      
      // Update the entire options array to avoid Firestore array-to-object conversion
      await updateDoc(pollRef, {
        options: updatedOptions
      });

      setHasVoted(true);
      toast.success('Vote recorded!');
    } catch (error) {
      console.error('Error voting:', error);
      toast.error('Failed to vote');
    }
  };

  const handleEndPoll = async () => {
    if (!activePoll || !isHost) return;

    try {
      const pollRef = doc(db, 'webrtcSessions', classId, 'polls', activePoll.id);
      await updateDoc(pollRef, {
        status: 'ended',
        endedAt: serverTimestamp()
      });

      toast.success('Poll ended');
    } catch (error) {
      console.error('Error ending poll:', error);
      toast.error('Failed to end poll');
    }
  };

  if (!activePoll || isDismissed) return null;

  // Ensure options is an array
  const pollOptions = Array.isArray(activePoll.options) 
    ? activePoll.options 
    : activePoll.options 
      ? Object.values(activePoll.options) 
      : [];

  if (!pollOptions || pollOptions.length === 0) return null;

  const totalVotes = pollOptions.reduce((sum, opt) => sum + (opt?.votes || 0), 0);
  const isPollEnded = activePoll.status === 'ended';
  const isPollActive = activePoll.status === 'active';
  
  // Format elapsed time
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed top-20 right-4 w-80 bg-gray-800 rounded-lg shadow-lg border border-gray-700 z-40">
      <div className="bg-gray-900 p-4 flex items-center justify-between border-b border-gray-700">
        <div className="flex items-center space-x-2">
          <BarChart3 size={20} />
          <h3 className="text-white font-semibold">
            {isPollEnded ? 'Poll Results' : 'Live Poll'}
          </h3>
        </div>
        <div className="flex items-center space-x-3">
          {isPollActive && (
            <div className="flex items-center space-x-1 text-yellow-400 text-sm">
              <Clock size={14} />
              <span>{formatTime(elapsedTime)}</span>
            </div>
          )}
          {isHost && isPollActive && (
            <button
              onClick={handleEndPoll}
              className="text-red-400 hover:text-red-300 text-sm"
            >
              End Poll
            </button>
          )}
          <button
            onClick={() => setIsDismissed(true)}
            className="text-gray-400 hover:text-white transition-colors"
            title="Close poll results"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      <div className="p-4">
        <h4 className="text-white font-medium mb-4">{activePoll.question}</h4>

        <div className="space-y-2">
          {pollOptions.map((option, index) => {
            const votes = option.votes || 0;
            const percentage = totalVotes > 0 ? (votes / totalVotes) * 100 : 0;
            const userVoted = option.voters && option.voters.includes(currentUser?.uid);
            
            // Show results only if:
            // 1. Poll is ended (everyone can see results)
            // 2. User is host (host can always see results)
            // Students cannot see results while poll is active, even after voting
            const canSeeResults = isPollEnded || isHost;

            return (
              <div key={index}>
                {!hasVoted && isPollActive && !isHost ? (
                  // Student hasn't voted yet - show voting buttons
                  <button
                    onClick={() => handleVote(index)}
                    className="w-full text-left bg-gray-700 hover:bg-gray-600 px-3 py-2 rounded-lg text-white transition-colors"
                  >
                    {option.text}
                  </button>
                ) : canSeeResults ? (
                  // Show full results (poll ended OR host)
                  <div className="bg-gray-700 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white text-sm">{option.text}</span>
                      <div className="flex items-center space-x-2">
                        {userVoted && <CheckCircle size={16} className="text-blue-400" />}
                        <span className="text-gray-400 text-sm">{votes} votes</span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-600 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <div className="text-gray-400 text-xs mt-1">{percentage.toFixed(1)}%</div>
                  </div>
                ) : (
                  // Student has voted but poll is still active - show which option they voted for (no results)
                  <div className={`bg-gray-700 rounded-lg p-3 ${userVoted ? 'border border-blue-500' : ''}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-white text-sm">{option.text}</span>
                      {userVoted && (
                        <div className="flex items-center space-x-1">
                          <CheckCircle size={16} className="text-blue-400" />
                          <span className="text-blue-400 text-xs">Your vote</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {totalVotes > 0 && (isPollEnded || isHost) && (
          <div className="mt-4 text-center text-gray-400 text-sm">
            Total votes: {totalVotes}
          </div>
        )}
        {isPollActive && !isHost && hasVoted && (
          <div className="mt-4 text-center text-yellow-400 text-xs">
            ✓ Your vote has been recorded. Results will be visible when the poll ends.
          </div>
        )}
      </div>
    </div>
  );
};

export default PollDisplay;

